import React, { useState, useEffect, useRef } from "react";
import "./Register.css";

import { generateId } from "../utils/generateId";
import { auth } from "../firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { useNavigate } from "react-router-dom";

// Helper: File → base64 data URL (safe for localStorage)
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function VendorRegister() {
  const navigate = useNavigate();

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    const verifier = new RecaptchaVerifier(auth, "recaptcha-container", {
      size: "normal"
    });
    verifier.render();
    recaptchaRef.current = verifier;
    window.recaptchaVerifier = verifier;

    return () => {
      try { verifier.clear(); } catch (_) {}
      recaptchaRef.current = null;
      window.recaptchaVerifier = null;
    };
  }, []);

  const [showImageOptions, setShowImageOptions] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    shopName: "",
    businessType: "",
    category: "",
    subcategory: "",
    address: "",
    subdistrict: "",
    password: "",
    confirmPassword: ""
  });

  const [lang, setLang] = useState("en");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [image, setImage] = useState(null);       // File
  const [preview, setPreview] = useState(null);    // URL for display

  const [subcategories, setSubcategories] = useState([]);

  const [showCategory, setShowCategory] = useState(false);
  const [showSubcategory, setShowSubcategory] = useState(false);
  const [showSubdistrict, setShowSubdistrict] = useState(false);

  // ================= CAMERA =================
  const openCamera = async () => {
    setShowImageOptions(false);
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode } });
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      alert("Camera permission denied or not supported");
    }
  };

  const stopCamera = () => {
    const video = videoRef.current;
    if (!video?.srcObject) return;
    video.srcObject.getTracks().forEach(track => track.stop());
    video.srcObject = null;
  };

  const flipCamera = async () => {
    stopCamera(); // stops tracks + nulls srcObject
    const newMode = facingMode === "environment" ? "user" : "environment";
    setFacingMode(newMode);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: newMode } });
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      alert("Camera switch failed");
    }
  };

  const capturePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
      setImage(file);
      setPreview(URL.createObjectURL(blob));
    });
    // Stop all tracks immediately so camera indicator light turns off
    const stream = video.srcObject;
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      video.srcObject = null;
    }
    setShowCamera(false);
  };

  const openGallery = () => {
    setShowImageOptions(false);
    if (fileInputRef.current) {
      fileInputRef.current.accept = "image/*";
      fileInputRef.current.click();
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  // ================= LABELS =================
  const text = {
    en: {
      title: "Vendor Register", register: "Register Vendor",
      name: "Full Name", phone: "Phone Number", email: "Email",
      shop: "Organisation", category: "Category", subcategory: "Subcategory",
      address: "Address", subdistrict: "Sub District",
      password: "Password", confirm: "Confirm Password"
    },
    kn: {
      title: "ಮಾರಾಟಗಾರ ನೋಂದಣಿ", register: "ಮಾರಾಟಗಾರ ನೋಂದಣಿ",
      name: "ಪೂರ್ಣ ಹೆಸರು", phone: "ಫೋನ್ ಸಂಖ್ಯೆ", email: "ಇಮೇಲ್",
      shop: "ಸಂಸ್ಥೆ", category: "ವರ್ಗ", subcategory: "ಉಪ ವರ್ಗ",
      address: "ವಿಳಾಸ", subdistrict: "ಉಪ ಜಿಲ್ಲೆ",
      password: "ಪಾಸ್‌ವರ್ಡ್", confirm: "ಪಾಸ್‌ವರ್ಡ್ ದೃಢೀಕರಿಸಿ"
    }
  };

  const categoryLabels = {
    Panchayat:        { en: "Panchayat",          kn: "ಪಂಚಾಯಿತಿಗಳು" },
    Agriculture:      { en: "Agriculture",         kn: "ಕೃಷಿ" },
    Milk:             { en: "Milk / Dairy",        kn: "ಹೈನುಗಾರಿಕೆ" },
    Health_Care:      { en: "Health Care",         kn: "ಆರೋಗ್ಯ" },
    Emergency:        { en: "Emergency Services",  kn: "ತುರ್ತು ಸೇವೆಗಳು" },
    Banking:          { en: "Banking",             kn: "ಬ್ಯಾಂಕಿಂಗ್" },
    Insurance:        { en: "Insurance",           kn: "ವಿಮೆ" },
    Digital_Services: { en: "Digital Services",   kn: "ಡಿಜಿಟಲ್ ಸೇವೆಗಳು" },
    District_Govt:    { en: "District Govt Officers", kn: "ಜಿಲ್ಲಾ ಸರ್ಕಾರಿ ಅಧಿಕಾರಿಗಳು" },
    Taluk_Govt:       { en: "Taluk Govt Officers",    kn: "ತಾಲ್ಲೂಕು ಸರ್ಕಾರಿ ಅಧಿಕಾರಿಗಳು" },
    Transport_Tourism:{ en: "Transportation & Tourism", kn: "ಸಾರಿಗೆ ಮತ್ತು ಪ್ರವಾಸೋದ್ಯಮ" }
  };

  const categoryData = {
    Panchayat:        { en: ["Zilla Panchayat","Taluk Panchayat","Grama Panchayat"], kn: ["ಜಿಲ್ಲಾ ಪಂಚಾಯತ್","ತಾಲ್ಲೂಕು ಪಂಚಾಯತ್","ಗ್ರಾಮ ಪಂಚಾಯತ್"] },
    Agriculture:      { en: ["RSK","Departments","Societies","Seeds","Fertilizers","APMC"], kn: ["ಆರ್‌ಎಸ್‌ಕೆ","ವಿಭಾಗಗಳು","ಸಂಸ್ಥೆಗಳು","ಬೀಜಗಳು","ರಸಗೊಬ್ಬರ","ಎಪಿಎಂಸಿ"] },
    Milk:             { en: ["Manmul","Veterinary","Feeds","Pet Pharma"], kn: ["ಮನ್‌ಮುಲ್","ಪಶುವೈದ್ಯಕೀಯ","ಮೇವು","ಪೆಟ್ ಫಾರ್ಮಾ"] },
    Health_Care:      { en: ["Hospitals","Diagnostics","Pharmacy","Doctors"], kn: ["ಆಸ್ಪತ್ರೆಗಳು","ಡಯಾಗ್ನೋಸ್ಟಿಕ್ಸ್","ಫಾರ್ಮಸಿ","ವೈದ್ಯರು"] },
    Emergency:        { en: ["Police","Ambulance","Fire"], kn: ["ಪೊಲೀಸ್","ಆಂಬ್ಯುಲೆನ್ಸ್","ಅಗ್ನಿಶಾಮಕ"] },
    Banking:          { en: ["Personal Loan","Home Loan","Gold Loan","Vehicle Loan","Education Loan","Other Banking Services"], kn: ["ವೈಯಕ್ತಿಕ ಸಾಲ","ಗೃಹ ಸಾಲ","ಚಿನ್ನದ ಸಾಲ","ವಾಹನ ಸಾಲ","ಶಿಕ್ಷಣ ಸಾಲ","ಇತರ ಬ್ಯಾಂಕಿಂಗ್ ಸೇವೆಗಳು"] },
    Insurance:        { en: ["Life Insurance","Term Insurance","Vehicle Insurance","Crop Insurance","Business Insurance"], kn: ["ಜೀವ ವಿಮೆ","ಅವಧಿ ವಿಮೆ","ವಾಹನ ವಿಮೆ","ಬೆಳೆ ವಿಮೆ","ವ್ಯಾಪಾರ ವಿಮೆ"] },
    Digital_Services: { en: ["Aadhaar Card","PAN Card","Voter ID","Online Application","Passport","Scholarship","RTC","Revenue Dept","Other Services"], kn: ["ಆಧಾರ್ ಕಾರ್ಡ್","ಪ್ಯಾನ್ ಕಾರ್ಡ್","ಮತದಾರ ಗುರುತು","ಆನ್‌ಲೈನ್ ಅರ್ಜಿ","ಪಾಸ್‌ಪೋರ್ಟ್","ವಿದ್ಯಾರ್ಥಿವೇತನ","ಆರ್‌ಟಿಸಿ","ರೆವೆನ್ಯೂ ಇಲಾಖೆ","ಇತರ ಸೇವೆಗಳು"] },
    District_Govt:    { en: ["DC Office","SP Office","DHO","DEO","ZP CEO","DFO","Other District Officers"], kn: ["ಡಿಸಿ ಕಚೇರಿ","ಎಸ್ಪಿ ಕಚೇರಿ","ಜಿಲ್ಲಾ ಆರೋಗ್ಯ ಅಧಿಕಾರಿ","ಜಿಲ್ಲಾ ಶಿಕ್ಷಣ ಅಧಿಕಾರಿ","ಜಿ.ಪಂ. ಸಿಇಒ","ಡಿಎಫ್‌ಒ","ಇತರ ಜಿಲ್ಲಾ ಅಧಿಕಾರಿಗಳು"] },
    Taluk_Govt:       { en: ["Tahsildar","Taluk Panchayat EO","BEO","Agriculture Officer","Health Inspector","Other Taluk Officers"], kn: ["ತಹಶೀಲ್ದಾರ್","ತಾ.ಪಂ. ಇಒ","ಬಿಇಒ","ಕೃಷಿ ಅಧಿಕಾರಿ","ಆರೋಗ್ಯ ನಿರೀಕ್ಷಕ","ಇತರ ತಾಲ್ಲೂಕು ಅಧಿಕಾರಿಗಳು"] },
    Transport_Tourism:{ en: ["Bus/Train/Flight Booking","Temple Darshana Booking","Domestic Tour Packages","International Tour Packages"], kn: ["ಬಸ್/ರೈಲು/ವಿಮಾನ ಬುಕ್ಕಿಂಗ್","ದೇವಾಲಯ ದರ್ಶನ ಬುಕ್ಕಿಂಗ್","ದೇಶೀಯ ಪ್ರವಾಸ ಪ್ಯಾಕೇಜ್","ಅಂತಾರಾಷ್ಟ್ರೀಯ ಪ್ರವಾಸ ಪ್ಯಾಕೇಜ್"] }
  };

  const subdistricts = {
    en: ["Mandya","Maddur","Malavalli","Nagamangala","Srirangapatna","Krishnarajapete","Pandavapura"],
    kn: ["ಮಂಡ್ಯ","ಮದ್ದೂರು","ಮಳವಳ್ಳಿ","ನಾಗಮಂಗಲ","ಶ್ರಿರಂಗಪಟ್ಟಣ","ಕೃಷ್ಣರಾಜಪೇಟೆ","ಪಾಂಡವಪುರ"]
  };

  useEffect(() => {
    if (form.category) {
      setSubcategories(categoryData[form.category][lang]);
    }
  }, [lang, form.category]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.password !== form.confirmPassword) {
      alert("Passwords do not match");
      return;
    }
    if (!form.phone || form.phone.length < 10) {
      alert("Enter a valid 10-digit phone number");
      return;
    }

    try {
      setLoading(true);

      const vendorId = await generateId("VND");

      // Convert image File to base64 so it survives JSON.stringify
      let imageBase64 = null;
      if (image) {
        imageBase64 = await fileToBase64(image);
      }

      // Store vendor data with base64 image in localStorage
      localStorage.setItem("vendorData", JSON.stringify({
        form: { ...form },
        image: imageBase64,
        id: vendorId
      }));

      const phoneNumber = "+91" + form.phone.replace(/\D/g, "").slice(-10);
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaRef.current);
      window.confirmationResult = confirmation;

      navigate("/otp");
    } catch(err) {
      // Reset widget so user can re-tick and retry
      try { recaptchaRef.current?.clear(); } catch (_) {}
      recaptchaRef.current = null;
      window.recaptchaVerifier = null;
      const verifier = new RecaptchaVerifier(auth, "recaptcha-container", { size: "normal" });
      verifier.render();
      recaptchaRef.current = verifier;
      window.recaptchaVerifier = verifier;
      alert(err.message || "OTP send failed ❌");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-header">
        <div className="lang-toggle">
          <button onClick={() => setLang("en")}>EN</button>
          <button onClick={() => setLang("kn")}>ಕನ್ನಡ</button>
        </div>

        <div className="camera-circle" onClick={() => setShowImageOptions(true)}>
          {preview ? (
            <img src={preview} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%" }} />
          ) : "📷"}
        </div>

        <input type="file" accept="image/*" ref={fileInputRef} style={{ display: "none" }} onChange={handleImageChange} />
        <h2>{text[lang].title}</h2>
      </div>

      {showImageOptions && (
        <div className="image-options" onClick={() => setShowImageOptions(false)}>
          <div className="image-box" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { openCamera(); setShowImageOptions(false); }}>📷 Take Photo</button>
            <button onClick={openGallery}>🖼️ Choose from Gallery</button>
          </div>
        </div>
      )}

      {showCamera && (
        <div className="camera-overlay">
          <video ref={videoRef} autoPlay playsInline className="camera-video" />
          <div className="camera-controls">
            <button onClick={openGallery}>🖼️</button>
            <button onClick={flipCamera}>🔄</button>
            <button onClick={capturePhoto} style={captureStyle}>📸</button>
            <button onClick={() => { stopCamera(); setShowCamera(false); }} style={btnStyle}>❌</button>
          </div>
        </div>
      )}

      <form className="register-form" onSubmit={handleSubmit}>
        <div className="input-box"><span>👤</span>
          <input name="name" placeholder={text[lang].name} onChange={handleChange} required />
        </div>
        <div className="input-box"><span>🏢</span>
          <input name="shopName" placeholder={text[lang].shop} onChange={handleChange} required />
        </div>
        <div className="input-box"><span>📞</span>
          <input name="phone" type="tel" maxLength="10" placeholder={text[lang].phone} onChange={handleChange} required />
        </div>
        <div className="input-box"><span>📧</span>
          <input name="email" type="email" placeholder={text[lang].email} value={form.email} onChange={handleChange} required />
        </div>

        {/* Category dropdown */}
        <div className="input-box dropdown" style={{ zIndex: showCategory ? 9999 : 1, position: "relative" }}>
          <span>📂</span>
          <input value={form.category ? categoryLabels[form.category][lang] : text[lang].category} readOnly />
          <span className="dropdown-icon" onClick={() => setShowCategory(!showCategory)}>⌄</span>
          {showCategory && (
            <div className="dropdown-menu">
              {Object.keys(categoryLabels).map((key) => (
                <div key={key} className="dropdown-item"
                  onClick={() => {
                    setForm({ ...form, category: key, subcategory: "" });
                    setSubcategories(categoryData[key][lang]);
                    setShowCategory(false);
                  }}>
                  {categoryLabels[key][lang]}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Subcategory dropdown */}
        <div className="input-box dropdown" style={{ zIndex: showSubcategory ? 9999 : 1, position: "relative" }}>
          <span>📦</span>
          <input value={form.subcategory || text[lang].subcategory} readOnly />
          <span className="dropdown-icon" onClick={() => form.category && setShowSubcategory(!showSubcategory)}>⌄</span>
          {showSubcategory && (
            <div className="dropdown-menu">
              {subcategories.map((item, i) => (
                <div key={i} className="dropdown-item"
                  onClick={() => { setForm({ ...form, subcategory: item }); setShowSubcategory(false); }}>
                  {item}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="input-box"><span>📍</span>
          <input name="address" placeholder={text[lang].address} onChange={handleChange} required />
        </div>

        {/* Subdistrict dropdown */}
        <div className="input-box dropdown" style={{ zIndex: showSubdistrict ? 9999 : 1, position: "relative" }}>
          <span>🗺️</span>
          <input value={form.subdistrict || text[lang].subdistrict} readOnly />
          <span className="dropdown-icon" onClick={() => setShowSubdistrict(!showSubdistrict)}>⌄</span>
          {showSubdistrict && (
            <div className="dropdown-menu">
              {subdistricts[lang].map((item, i) => (
                <div key={i} className="dropdown-item"
                  onClick={() => { setForm({ ...form, subdistrict: item }); setShowSubdistrict(false); }}>
                  {item}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="input-box"><span>🔒</span>
          <input type={showPass ? "text" : "password"} name="password" placeholder={text[lang].password} onChange={handleChange} required />
          <button type="button" onClick={() => setShowPass(!showPass)}>👁️</button>
        </div>

        <div className="input-box"><span>🔒</span>
          <input type={showConfirm ? "text" : "password"} name="confirmPassword" placeholder={text[lang].confirm} onChange={handleChange} required />
          <button type="button" onClick={() => setShowConfirm(!showConfirm)}>👁️</button>
        </div>

        <div id="recaptcha-container"></div>

        <button type="submit" className="register-btn" disabled={loading}>
          {loading ? "Sending OTP..." : text[lang].register}
        </button>
      </form>

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
}

const btnStyle = {
  width: 50, height: 50, borderRadius: "50%",
  border: "none", background: "rgba(255,255,255,0.8)", fontSize: "20px"
};

const captureStyle = {
  width: 70, height: 70, borderRadius: "50%",
  border: "4px solid #ddd", background: "#fff", fontSize: "22px"
};
